import { Component, computed, inject, signal, ViewChild } from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';
import { DynamicFlowSandbox } from '../../sandboxes/dynamic-flow.sandbox';
import { DynamicFlowLocationAnswer, DynamicFlowOption, DynamicFlowRawFormValues, DynamicFlowStepType } from '../../model/dynamic-flow.types';
import { SingleSelectComponent } from '../../components/single-select/single-select.component';
import { MultiSelectComponent } from '../../components/multi-select/multi-select.component';
import { ContentStepComponent } from '../../components/content-step/content-step.component';
import { LocationStepComponent } from '../../components/location-step/location-step.component';
import { FormStepComponent } from '../../components/form-step/form-step.component';
import { AutocompleteItemModel } from '@app/shared/components/autocomplete/autocomplete-data.model';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { defaultLocationToAnswer } from '../../utils/dynamic-flow-location.util';

@Component({
  selector: 'c-dynamic-flow-step',
  imports: [SingleSelectComponent, MultiSelectComponent, ContentStepComponent, LocationStepComponent, FormStepComponent, TranslocoPipe],
  templateUrl: './dynamic-flow-step.component.html',
  styleUrl: './dynamic-flow-step.component.scss',
})
export class DynamicFlowStepComponent implements ViewWillEnter, ViewWillLeave {
  private readonly dynamicFlowSandbox = inject(DynamicFlowSandbox);

  @ViewChild(LocationStepComponent) private locationStep: LocationStepComponent;

  step = this.dynamicFlowSandbox.currentStep;
  question = computed(() => this.step()?.step.question);
  answer = computed(() => this.step()?.answer);
  resolvedLocation = computed<DynamicFlowLocationAnswer | null>(() => {
    const step = this.step();
    return step?.answer?.location ?? defaultLocationToAnswer(step?.step.location?.defaultLocation);
  });
  formControls = this.dynamicFlowSandbox.formControls;
  dynamicForm = this.dynamicFlowSandbox.dynamicForm;
  locationSuggestions = signal<AutocompleteItemModel[]>([]);
  currentLocationUsed = signal(false);
  showValidationMessage = this.dynamicFlowSandbox.showValidationMessage;
  canView = signal(false);

  SINGLE_SELECT_QUESTION = DynamicFlowStepType.SINGLE_SELECT_QUESTION;
  MULTI_SELECT_QUESTION = DynamicFlowStepType.MULTI_SELECT_QUESTION;
  LOCATION = DynamicFlowStepType.LOCATION;
  CONTENT = DynamicFlowStepType.CONTENT;
  FORM = DynamicFlowStepType.FORM;

  ionViewWillEnter(): void {
    this.canView.set(true);
  }

  ionViewWillLeave(): void {
    this.canView.set(false);
  }

  onOptionsSelected(answer: DynamicFlowOption | DynamicFlowOption[]): void {
    this.dynamicFlowSandbox.updateAnswer(Array.isArray(answer) ? answer : [answer]);
  }

  async onLocationSearch(value: string): Promise<void> {
    this.currentLocationUsed.set(false);
    const predictions = await this.dynamicFlowSandbox.getPlacePredictions(value);
    this.locationSuggestions.set(predictions.map((p) => ({ id: p.place_id, label: p.description, value: p.structured_formatting.main_text })));
  }

  async onLocationSelected(item: AutocompleteItemModel): Promise<void> {
    this.locationSuggestions.set([]);
    const details = await this.dynamicFlowSandbox.getPlaceDetails(item.id);
    if (details) {
      this.locationStep.updatePlaceDetails(details);
      this.currentLocationUsed.set(false);
    }
  }

  onLocationChanged(location: DynamicFlowLocationAnswer | null): void {
    this.dynamicFlowSandbox.updateLocationAnswer(location);
  }

  onFormChanged(form: DynamicFlowRawFormValues | null): void {
    this.dynamicFlowSandbox.updateFormAnswer(form);
  }

  async onUseCurrentLocation(): Promise<void> {    
    const details = await this.dynamicFlowSandbox.getCurrentLocationDetails();
    if (!details) return;
    this.locationStep.applyCurrentLocation(details);
    this.locationSuggestions.set([]);
    this.currentLocationUsed.set(true);
  }
}
